import { Expand } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/shared/lib/cn";

type RankingEntry = {
  name: string;
  count: number;
};

type RankingColumn = {
  title: string;
  entries: RankingEntry[];
};

type EnterpriseDashboardRankingCardProps = {
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  iconClassName: string;
  columns: RankingColumn[];
  badgeClassName: string;
  viewAllLabel?: string;
  detailHref?: string;
};

function RankingList({
  entries,
  badgeClassName,
}: {
  entries: RankingEntry[];
  badgeClassName: string;
}) {
  return (
    <div className="space-y-2">
      {entries.map((entry, index) => (
        <div key={`${entry.name}-${index}`} className="flex items-center justify-between gap-2 py-1">
          <p className="min-w-0 truncate text-xs text-primary">
            {index + 1}. {entry.name}
          </p>
          <span
            className={cn(
              "inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-2 text-[11px] font-semibold",
              badgeClassName,
            )}
          >
            {entry.count}
          </span>
        </div>
      ))}
    </div>
  );
}

export function EnterpriseDashboardRankingCard({
  title,
  icon: Icon,
  iconClassName,
  columns,
  badgeClassName,
  viewAllLabel,
  detailHref,
}: EnterpriseDashboardRankingCardProps) {
  const card = (
    <div
      className={cn(
        "theme-card flex h-full flex-col p-5",
        detailHref && "cursor-pointer transition-shadow hover:shadow-md",
      )}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              iconClassName,
            )}
          >
            <Icon size={20} strokeWidth={2} />
          </div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {viewAllLabel && detailHref ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
            {viewAllLabel}
            <Expand size={13} />
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {columns.map((column) => (
          <div key={column.title}>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
              {column.title}
            </p>
            <RankingList entries={column.entries} badgeClassName={badgeClassName} />
          </div>
        ))}
      </div>
    </div>
  );

  if (detailHref) {
    return (
      <Link to={detailHref} className="block h-full no-underline">
        {card}
      </Link>
    );
  }

  return card;
}
